// ============================================================
// PhysicsBody — AABB rigid body
// ============================================================
import { GRAVITY, MAX_FALL_SPEED } from '../constants.js';

export class PhysicsBody {
  /**
   * @param {object} opts
   * @param {number} opts.x - centre x
   * @param {number} opts.y - centre y
   * @param {number} opts.width
   * @param {number} opts.height
   * @param {boolean} [opts.isStatic]
   * @param {number}  [opts.mass]        kg (relative)
   * @param {number}  [opts.bounciness]  0-1
   * @param {number}  [opts.friction]    0-1
   * @param {boolean} [opts.isTrigger]   - no physical response, just events
   * @param {string}  [opts.material]    - 'default'|'ice'|'bouncy'|'sticky'|'lava'|'water'
   * @param {boolean} [opts.noGravity]
   * @param {number}  [opts.gravityScale]
   * @param {boolean} [opts.canFloat]    - buoyant in water
   */
  constructor(opts = {}) {
    this.x = opts.x ?? 0;
    this.y = opts.y ?? 0;
    this.width  = opts.width  ?? 40;
    this.height = opts.height ?? 40;

    this.vx = 0;
    this.vy = 0;
    this.ax = 0;  // extra acceleration this frame
    this.ay = 0;

    this.isStatic    = opts.isStatic   ?? false;
    this.isTrigger   = opts.isTrigger  ?? false;
    this.mass        = opts.mass       ?? 1;
    this.bounciness  = opts.bounciness ?? 0;
    this.friction    = opts.friction   ?? 0.82;
    this.material    = opts.material   ?? 'default';
    this.gravityScale= opts.gravityScale ?? 1;
    this.noGravity   = opts.noGravity  ?? false;
    this.canFloat    = opts.canFloat   ?? false;

    this.onGround    = false;
    this.onWall      = false;
    this.inWater     = false;

    // Sleeping — body stops simulating when at rest, wakes on impact
    this.sleeping       = false;
    this._sleepTimer    = 0;
    this._sleepThresh   = 0.5;   // seconds of stillness before sleep

    // collision callbacks
    this.onCollide   = null;  // fn(other, normal)
    this.onTrigger   = null;  // fn(other)

    // owner entity reference
    this.entity      = null;

    // one-shot flags
    this._destroyNextFrame = false;
  }

  /** AABB left edge */
  get left()   { return this.x - this.width / 2; }
  /** AABB right edge */
  get right()  { return this.x + this.width / 2; }
  /** AABB top edge */
  get top()    { return this.y - this.height / 2; }
  /** AABB bottom edge */
  get bottom() { return this.y + this.height / 2; }

  /** Wake this body from sleep (called on collision or impulse) */
  wake() {
    this.sleeping    = false;
    this._sleepTimer = 0;
  }

  /** Update sleep state — call after velocity is set */
  _updateSleep(dt) {
    if (this.isStatic || this._noSleep) return;
    const still = Math.abs(this.vx) < 4 && Math.abs(this.vy) < 4 && this.onGround;
    if (still) {
      this._sleepTimer += dt;
      if (this._sleepTimer > this._sleepThresh) this.sleeping = true;
    } else {
      this._sleepTimer = 0;
      this.sleeping    = false;
    }
  }

  /** Step the body forward in time (called by PhysicsEngine) */
  step(dt) {
    if (this.isStatic || this.sleeping) return;

    // gravity
    if (!this.noGravity) {
      const g = GRAVITY * this.gravityScale * (this.inWater ? 0.35 : 1);
      this.vy += g * dt;
    }

    // extra acceleration
    this.vx += this.ax * dt;
    this.vy += this.ay * dt;
    this.ax = 0;
    this.ay = 0;

    // clamp fall speed
    if (this.vy > MAX_FALL_SPEED) this.vy = MAX_FALL_SPEED;

    // water drag
    if (this.inWater) {
      this.vx *= 0.92;
      this.vy *= 0.88;
    }

    this.x += this.vx * dt;
    this.y += this.vy * dt;
  }

  /** Apply impulse (immediately changes velocity) */
  applyImpulse(ix, iy) {
    if (this.isStatic) return;
    this.wake();
    this.vx += ix / this.mass;
    this.vy += iy / this.mass;
  }

  overlaps(other) {
    return (
      this.left   < other.right  &&
      this.right  > other.left   &&
      this.top    < other.bottom &&
      this.bottom > other.top
    );
  }
}
